import SearchOutlined from '@mui/icons-material/SearchOutlined';
import type { AutocompleteChangeReason, FilterOptionsState } from '@mui/material';
import { Autocomplete, Card, Chip, createFilterOptions, Stack, styled, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form-mui';
import { SelectElement, useFieldArray, useFormContext } from 'react-hook-form-mui';

type SelectProps = {
  name: string;
  label: string;
  options:
    | string[]
    | {
        label: string;
        id: string | number;
      }[];
};

type TextFieldProps = {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
};

type MultiSelectProps = SelectProps & {
  // required by default
  required?: boolean;
  // multi-select/input fields
  subOptions?: (SelectProps | TextFieldProps)[];
};

type OptionType = string | Record<string, any>;

const thirdChildFullWidth = {
  '&:nth-child(n+3)': {
    flexBasis: '100%',
  },
};

const StyledSelectElement = styled(SelectElement)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  ...thirdChildFullWidth,
}));

const StyledTextField = styled(TextField)(() => thirdChildFullWidth);

// Access nested error correctly using get helper
const getNestedError = (errors: unknown, path: string) => {
  return path.split('.').reduce(
    (obj: Record<string, any> | undefined, key) => {
      return obj && typeof obj === 'object' ? obj[key] : undefined;
    },
    errors as Record<string, any> | undefined,
  );
};

export function MultiSelect({ name, label, options, required = true, subOptions }: MultiSelectProps) {
  const {
    control,
    getValues,
    register,
    unregister,
    formState: { errors },
    trigger,
    getFieldState,
  } = useFormContext();

  const { fields, append, remove } = useFieldArray<FieldValues>({
    control,
    name,
  });

  const rules = required ? { required: true } : {};

  const formValues = getValues(name) ?? [];
  const defaultValues = Array.isArray(formValues) ? formValues.map((item: { name: string }) => item.name) : [];
  const [selectedOptions, setSelectedOptions] = useState<string[]>(defaultValues);
  const [inputValue, setInputValue] = useState(''); // Input text

  // filter out options that have already been selected
  const filteredOptions = options.filter((option: OptionType) => {
    if (typeof option === 'string') {
      return !selectedOptions.includes(option);
    }

    return !Object.values(option).some((val) => selectedOptions.includes(String(val)));
  });

  // input name for the query field
  const inputName = `${name}.query`;

  // when an option is selected, add it to both state and RHF field array
  const handleChange = (_event: React.SyntheticEvent, value: unknown, reason: AutocompleteChangeReason) => {
    setInputValue('');

    if (value && reason === 'selectOption') {
      const optionValue = typeof value === 'string' ? value : (value as { label: string }).label;
      setSelectedOptions((prev) => [...prev, optionValue]);

      // Only map subOptions if it exists, default to empty array
      const fieldNames = subOptions?.map((item) => item.name) ?? [];

      // create an empty object using fieldNames as keys
      const emptyValues = fieldNames.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

      // append a new field array into RHF
      append({ ...emptyValues, name: optionValue });
    }
  };

  // remove selected option from both state and RHF field array
  const onRemove = (option: string, index: number) => {
    const filteredValues = selectedOptions.filter((item) => item !== option);
    setSelectedOptions(() => filteredValues);
    // remove field array from RHF
    remove(index);
  };

  const filterOptions = (options: unknown[], params: FilterOptionsState<unknown>) => {
    const filtered = createFilterOptions()(options, params);

    if (filtered.length === 0 && options.length > 0) {
      // Add the last option in the options array when no results match
      filtered.push(options[options.length - 1]);
    }
    return filtered;
  };

  // if no option is selected, make the field required to prevent step to progress to next
  const isRequired = !selectedOptions.length;

  // unregister query input if not required
  useEffect(() => {
    if (!isRequired) {
      unregister(inputName);
    }
  }, [inputName, isRequired, unregister]);

  return (
    <Stack sx={{ mb: 4 }}>
      <Stack
        sx={{
          gap: 2,
          flexDirection: 'row',
          flexFlow: 'row wrap',
          mb: 4,
        }}
      >
        {selectedOptions.map((option, index) => (
          <Chip label={option} key={option} onDelete={() => onRemove(option, index)} />
        ))}
      </Stack>
      <>
        <Autocomplete
          freeSolo
          value={inputValue}
          inputValue={inputValue}
          disablePortal
          options={filteredOptions}
          onChange={handleChange}
          filterOptions={filterOptions}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue); // Update input as user types
          }}
          renderInput={(params) => (
            <TextField
              variant="filled"
              {...params}
              sx={{ '.MuiFilledInput-root': { py: 1 } }}
              placeholder={label}
              error={!!getNestedError(errors, inputName)}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: <SearchOutlined />,
                },
              }}
            />
          )}
        />
        <input {...register(inputName, isRequired ? rules : {})} value={inputValue} hidden />
        {subOptions &&
          (fields as FieldValues[]).map((item, index) => (
            <Card key={item.id} sx={{ p: 4, mt: 4 }}>
              <Stack sx={{ justifyContent: 'space-between', mb: 2, flexDirection: 'row' }}>
                <Typography variant="body1" component="h3">
                  {item.name}
                </Typography>
              </Stack>
              <input {...register(`${name}[${index}].name`, { required: true })} hidden />

              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  flexFlow: { md: 'row wrap' },
                  gap: 4,
                }}
              >
                {subOptions.map((detail) => {
                  const fieldName = `${name}[${index}].${detail.name}`;
                  const { isDirty, invalid } = getFieldState(fieldName);
                  if ('options' in detail) {
                    return (
                      <StyledSelectElement
                        control={control}
                        rules={{ required: true }}
                        key={fieldName}
                        name={fieldName}
                        label={detail.label}
                        options={detail.options}
                        error={!!getNestedError(errors, fieldName)}
                        color={isDirty && !invalid ? 'success' : 'primary'}
                        onChange={() => void trigger(fieldName)}
                      />
                    );
                  }
                  return (
                    <StyledTextField
                      {...register(fieldName, { required: true })}
                      key={fieldName}
                      label={detail.label}
                      placeholder={detail.placeholder}
                      error={!!getNestedError(errors, fieldName)}
                      onChange={() => void trigger(fieldName)}
                      color={isDirty && !invalid ? 'success' : 'primary'}
                    />
                  );
                })}
              </Stack>
            </Card>
          ))}
      </>
    </Stack>
  );
}
